import { useCallback, useMemo, useState } from "react";
import { useRouter } from "expo-router";
import { useSiteCameraKeys } from "@src/features/cameras/hooks/useSiteCameraKeys";
import { getCameraKeyString } from "@src/utils/key-parser";
import { useAlarmsByCameras } from "@src/services/api/hooks";
import { showSuccessToast, showErrorToast } from "@src/utils/toast";
import { api } from "@src/services/api/client";
import { useTenantId } from "@src/features/tenants";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AlarmResponse } from "@src/services/api/types";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";

export type ResponseState = {
  sirens: "on" | "off";
  strobes: "on" | "off";
  monitoring: "on" | "off";
};

type ResponseOverride = Partial<ResponseState>;
type ResponseField = keyof ResponseState;

const DEFAULT_RESPONSE: ResponseState = { sirens: "off", strobes: "off", monitoring: "off" };
const RESPONSE_FIELDS: readonly ResponseField[] = ["sirens", "strobes", "monitoring"];

export function useManageAlarmResponse() {
  const router = useRouter();
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const { track } = useAmplitude();
  const { siteCameras, cameraKeys, selectedSiteId } = useSiteCameraKeys();

  const effectiveSiteId = selectedSiteId ?? "";
  const alarmQueries = useAlarmsByCameras(effectiveSiteId, cameraKeys);

  const isLoading = alarmQueries.some((q) => q.isLoading);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showRequestSentModal, setShowRequestSentModal] = useState(false);
  // Store per-field deltas only — an override that captured a full snapshot of
  // the state would overwrite real server data arriving after the user toggled
  // a checkbox, since initialResponses falls back to "off" while alarm queries
  // are still loading.
  const [overrides, setOverrides] = useState<Map<string, ResponseOverride>>(new Map());

  // Build initial response state from alarm data
  const initialResponses = useMemo(() => {
    const map = new Map<string, ResponseState>();
    for (let i = 0; i < cameraKeys.length; i++) {
      const config = alarmQueries[i]?.data;
      const response = config?.settings?.response;
      map.set(cameraKeys[i], {
        sirens: response?.sirens ?? "off",
        strobes: response?.strobes ?? "off",
        monitoring: response?.monitoring ?? "off",
      });
    }
    return map;
  }, [cameraKeys, alarmQueries]);

  // Merge initial with local overrides (per-field)
  const effectiveResponses = useMemo(() => {
    const map = new Map(initialResponses);
    for (const [key, override] of overrides) {
      const initial = map.get(key) ?? DEFAULT_RESPONSE;
      map.set(key, { ...initial, ...override });
    }
    return map;
  }, [initialResponses, overrides]);

  const toggleResponse = useCallback(
    (cameraKey: string, field: ResponseField) => {
      setOverrides((prev) => {
        const next = new Map(prev);
        const currentOverride = next.get(cameraKey) ?? {};
        const effectiveCurrent = currentOverride[field] ?? initialResponses.get(cameraKey)?.[field] ?? "off";
        next.set(cameraKey, {
          ...currentOverride,
          [field]: effectiveCurrent === "on" ? "off" : "on",
        });
        return next;
      });
    },
    [initialResponses],
  );

  const allSirensOn = useMemo(
    () => cameraKeys.length > 0 && cameraKeys.every((k) => (effectiveResponses.get(k)?.sirens ?? "off") === "on"),
    [cameraKeys, effectiveResponses],
  );

  const allStrobesOn = useMemo(
    () => cameraKeys.length > 0 && cameraKeys.every((k) => (effectiveResponses.get(k)?.strobes ?? "off") === "on"),
    [cameraKeys, effectiveResponses],
  );

  const toggleAllSirens = useCallback(() => {
    const newValue = allSirensOn ? "off" : "on";
    setOverrides((prev) => {
      const next = new Map(prev);
      for (const key of cameraKeys) {
        next.set(key, { ...(next.get(key) ?? {}), sirens: newValue });
      }
      return next;
    });
  }, [allSirensOn, cameraKeys]);

  const toggleAllStrobes = useCallback(() => {
    const newValue = allStrobesOn ? "off" : "on";
    setOverrides((prev) => {
      const next = new Map(prev);
      for (const key of cameraKeys) {
        next.set(key, { ...(next.get(key) ?? {}), strobes: newValue });
      }
      return next;
    });
  }, [allStrobesOn, cameraKeys]);

  // Monitoring: only show if at least one camera has monitoring "on"
  const showMonitoring = useMemo(
    () => cameraKeys.some((k) => (effectiveResponses.get(k)?.monitoring ?? "off") === "on"),
    [cameraKeys, effectiveResponses],
  );

  const allMonitoringOn = useMemo(
    () => cameraKeys.length > 0 && cameraKeys.every((k) => (effectiveResponses.get(k)?.monitoring ?? "off") === "on"),
    [cameraKeys, effectiveResponses],
  );

  const toggleAllMonitoring = useCallback(() => {
    const newValue = allMonitoringOn ? "off" : "on";
    setOverrides((prev) => {
      const next = new Map(prev);
      for (const key of cameraKeys) {
        next.set(key, { ...(next.get(key) ?? {}), monitoring: newValue });
      }
      return next;
    });
  }, [allMonitoringOn, cameraKeys]);

  // Detect changes — compare only the fields the user actually overrode
  const hasChanges = useMemo(() => {
    for (const [key, override] of overrides) {
      const initial = initialResponses.get(key) ?? DEFAULT_RESPONSE;
      for (const field of RESPONSE_FIELDS) {
        const overrideValue = override[field];
        if (overrideValue !== undefined && overrideValue !== initial[field]) return true;
      }
    }
    return false;
  }, [overrides, initialResponses]);

  const updateResponses = useMutation({
    mutationFn: async () => {
      if (!selectedSiteId) return;

      const camerasToUpdate: { cameraKey: string; response: AlarmResponse }[] = [];

      for (const [cameraKey, override] of overrides) {
        const initial = initialResponses.get(cameraKey);
        if (!initial) continue;

        const effective: ResponseState = { ...initial, ...override };
        if (
          effective.sirens === initial.sirens &&
          effective.strobes === initial.strobes &&
          effective.monitoring === initial.monitoring
        )
          continue;

        const alarmIndex = cameraKeys.indexOf(cameraKey);
        const config = alarmQueries[alarmIndex]?.data;
        const currentResponse = config?.settings?.response;

        camerasToUpdate.push({
          cameraKey,
          response: {
            recording: currentResponse?.recording ?? "on",
            monitoring: effective.monitoring,
            sirens: effective.sirens,
            strobes: effective.strobes,
          },
        });
      }

      const results = await Promise.allSettled(
        camerasToUpdate.map(async ({ cameraKey, response }) => {
          const result = await api.alarms.updateResponse(tenantId, selectedSiteId!, cameraKey, response);
          queryClient.setQueryData(["alarms", tenantId, selectedSiteId, cameraKey], result);
          return result;
        }),
      );

      const failures = results.filter((r) => r.status === "rejected");
      if (failures.length > 0) {
        throw new Error(`Failed to update ${failures.length} cameras`);
      }
    },
    onSuccess: () => {
      showSuccessToast({ message: "Alarm response updated" });
      router.back();
    },
    onError: (error) => {
      showErrorToast({ message: "Error", description: "Failed to update alarm response." });
      console.error("Failed to update alarm response:", error);
    },
  });

  const isSaving = updateResponses.isPending;

  const save = useCallback(() => updateResponses.mutate(), [updateResponses]);

  const handleUpgradeRequest = useCallback(() => {
    track(ANALYTICS_EVENTS.MONITORING_UPGRADE_REQUESTED, {
      tenant_id: tenantId,
      site_id: selectedSiteId,
    });
    setShowUpgradeModal(false);
    setShowRequestSentModal(true);
  }, [track, tenantId, selectedSiteId]);

  return {
    siteCameras,
    cameraKeys,
    selectedSiteId,
    tenantId,
    isLoading,
    effectiveResponses,
    toggleResponse,
    allSirensOn,
    allStrobesOn,
    allMonitoringOn,
    toggleAllSirens,
    toggleAllStrobes,
    toggleAllMonitoring,
    showMonitoring,
    hasChanges,
    isSaving,
    save,
    showUpgradeModal,
    setShowUpgradeModal,
    showRequestSentModal,
    setShowRequestSentModal,
    handleUpgradeRequest,
  };
}
